import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Typography,
  Divider,
} from "@mui/material";
import PaletteIcon from "@mui/icons-material/Palette";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import LightModeIcon from "@mui/icons-material/LightMode";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
// import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
// import EmailIcon from "@mui/icons-material/Email";
// import NotificationsOffIcon from "@mui/icons-material/NotificationsOff";
import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
// import OneSignal from "react-onesignal";
import { useTheme as useAppTheme, ThemeName } from "../contexts/ThemeContext";
import { DialogControl } from "../controls/DialogControl";

interface PersonalSettingsDialogProps {
  open: boolean;
  onClose: () => void;
}

export function PersonalSettingsDialog(props: PersonalSettingsDialogProps) {
  const { open, onClose } = props;
  const { t } = useTranslation();
  const { themeName, setThemeName } = useAppTheme();

  const [localTheme, setLocalTheme] = useState<ThemeName>(themeName);

  useEffect(() => {
    // Sync with current theme when dialog opens
    setLocalTheme(themeName);
  }, [open, themeName]);

  const saveSettings = () => {
    // Apply theme change
    setThemeName(localTheme);
    // TODO: Sync to cloud via playerState
    onClose();
  };

  // const handleNotificationModeChange = (
  //   mode: "off" | "push" | "email" | "both"
  // ) => {
  //   setSettings({ ...settings, notificationMode: mode });

  //   // If enabling push, prompt for subscription
  //   if ((mode === "push" || mode === "both") && !pushSubscribed) {
  //     OneSignal.Slidedown.promptPush();
  //   }
  // };

  return (
    <DialogControl
      open={open}
      onClose={onClose}
      dialogProps={{ maxWidth: "sm" }}
      paperSx={{
        borderRadius: 3,
        // zentriertes, nicht-fullscreen Layout für Einstellungen
        width: { xs: "auto", sm: "auto" },
        maxHeight: { xs: "90vh", sm: "90vh" },
        minHeight: "auto",
        margin: { xs: "auto", sm: "auto" },
      }}
      title={
        <Typography variant="h6" fontWeight={600}>
          {t("PersonalSettings.title")}
        </Typography>
      }
    >
      <Box sx={{ p: 3, pt: 4 }}>
        {/* Theme Settings */}
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="subtitle1"
            fontWeight={600}
            color="primary.main"
            gutterBottom
            sx={{ display: "flex", alignItems: "center", gap: 1 }}
          >
            <PaletteIcon fontSize="small" />
            {t("PersonalSettings.themeSettings")}
          </Typography>
          <Divider sx={{ mb: 2, borderColor: "text.secondary" }} />

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel sx={{ color: "text.secondary" }} id="theme-label">
              {t("PersonalSettings.selectTheme")}
            </InputLabel>
            <Select
              labelId="theme-label"
              value={localTheme}
              onChange={(e) => setLocalTheme(e.target.value as ThemeName)}
              label={t("PersonalSettings.selectTheme")}
              sx={{
                color: "text.primary",
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "text.secondary",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "primary.main",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "primary.main",
                },
              }}
            >
              <MenuItem value="classic">
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <AutoFixHighIcon fontSize="small" />
                  {t("PersonalSettings.themeClassic")}
                </Box>
              </MenuItem>
              <MenuItem value="modern">
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <LightModeIcon fontSize="small" />
                  {t("PersonalSettings.themeModern")}
                </Box>
              </MenuItem>
              <MenuItem value="dark">
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <DarkModeIcon fontSize="small" />
                  {t("PersonalSettings.themeDark")}
                </Box>
              </MenuItem>
            </Select>
            <FormHelperText sx={{ color: "text.secondary", opacity: 0.8 }}>
              {t("PersonalSettings.themeHelper")}
            </FormHelperText>
          </FormControl>

          {/* Theme Preview */}
          <Box
            sx={{
              p: 2,
              borderRadius: 2,
              border: "2px solid",
              borderColor: "divider",
              bgcolor: "action.hover",
            }}
          >
            <Typography
              variant="body2"
              color="text.primary"
              sx={{ opacity: 0.9 }}
            >
              {localTheme === "classic" &&
                t("PersonalSettings.themeClassicDescription")}
              {localTheme === "modern" &&
                t("PersonalSettings.themeModernDescription")}
              {localTheme === "dark" &&
                t("PersonalSettings.themeDarkDescription")}
            </Typography>
          </Box>
        </Box>

        {/* Notification Settings - Coming Soon */}
        {/* <Box sx={{ mb: 3 }}>
          <Typography
            variant="subtitle1"
            fontWeight={600}
            color="primary.main"
            gutterBottom
            sx={{ display: "flex", alignItems: "center", gap: 1 }}
          >
            <NotificationsActiveIcon fontSize="small" />
            {t("PersonalSettings.notificationSettings")}
          </Typography>
          <Divider sx={{ mb: 2, borderColor: "text.secondary" }} />
          <Typography variant="body2" color="text.secondary">
            {t("PersonalSettings.comingSoon")}
          </Typography>
        </Box> */}

        {/* Save Button */}
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
          <Button
            onClick={onClose}
            sx={{
              color: "text.secondary",
              "&:hover": {
                bgcolor: "text.secondary",
              },
            }}
          >
            {t("PersonalSettings.cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={saveSettings}
            sx={{
              bgcolor: "primary.main",
              color: "primary.contrastText",
              "&:hover": {
                bgcolor: "primary.dark",
              },
            }}
          >
            {t("PersonalSettings.save")}
          </Button>
        </Box>
      </Box>
    </DialogControl>
  );
}
